import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Accordion } from '@/components/common/Accordion';
import { OuterWrapper } from '@/components/common/Accordion/AccordionItemConfig';
import { screen } from '@/components/common/breakpoints';
import { BulletPoints } from '@/components/common/bulletpoints';
import { Button } from '@/components/common/Buttons';
import { Divider } from '@/components/common/Divider';
import { HeadingLevelProvider } from '@/components/common/Heading/context';
import { Paragraph } from '@/components/common/MarkUp';
import { TitleWithIcon } from '@/components/common/TitleWithIcon';
import { PortableText } from '@/components/common/utils';
import { Stack } from '@/components/layout/Stack';
import { ProgrammeCertificate } from '@/components/page/courses/Sidebar/ProgrammeCertificate';
import { blockUrlWithAudience } from '@/lib/utils';
import { bodyMedium, bodySmall, labelLarge } from '@/styles/typography';

import type { ProgrammeB2C } from '@/sanity/sanity.types';
import type { BlockWithAudience, CommonCTAProps } from '@/types/shared';

export interface SidebarProps extends BlockWithAudience {
  footer?: Omit<ProgrammeB2C['sidebar']['footer'], 'CTA'> & {
    CTA?: CommonCTAProps;
  };
  tabs?: ProgrammeB2C['sidebar']['tabs'];
}
export const Sidebar = ({
  tabs,
  footer,
  pageAudience,
  isAudienceSwitcherEnabled,
}: SidebarProps) => {
  const [openItems, setOpenItems] = useState([0]);
  const [accordionItems, setAccordionItems] = useState([]);

  const renderContent = (tab) => {
    const components = tab.content.map((content) => {
      switch (content._type) {
        case 'certificate':
          return (
            <ProgrammeCertificate
              key={content._key}
              {...content}
              pageAudience={pageAudience}
              isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
            />
          );
        case 'titleWithIcon':
          return (
            <HeadingLevelProvider level={3}>
              <TitleWithIcon
                key={content._key}
                iconName={content.icon?.name}
                heading={content.title.heading}
                subheading={content.title?.subheading}
                size="md"
                variant="component"
                iconVariant="standard"
                alignment="left"
                mobileAlignment="left"
                {...content}
              />
            </HeadingLevelProvider>
          );
        case 'freeText':
          return (
            <StyledBulletPoints
              key={content._key}
              pointColour="--icon-warm-base"
              bulletPointStyle="check"
              iconSize={16}
            >
              <PortableText
                value={content.richText}
                audienceConfig={{ pageAudience, isAudienceSwitcherEnabled }}
              />
            </StyledBulletPoints>
          );
        default:
          return null;
      }
    });

    return {
      heading: tab.title,
      component: (
        <ComponentsContainer key={tab?._key} spacing="--space-component-xl">
          {components}
        </ComponentsContainer>
      ),
      _key: tab?._key,
      isExpandable: tabs.length > 1,
    };
  };

  useEffect(() => {
    if (tabs) {
      const tabItems = tabs.map((tab) => renderContent(tab));
      setAccordionItems(tabItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);

  return (
    tabs?.length > 0 && (
      <SidebarContainer>
        {accordionItems.length > 0 && (
          <AccordionContainer isAccordionPresent={accordionItems.length > 1}>
            <Accordion
              accordionItems={accordionItems}
              openItemsState={[openItems, setOpenItems]}
              chevronSize="12px"
            />
          </AccordionContainer>
        )}
        {(!!footer?.price || footer?.CTA?.url) && (
          <>
            {accordionItems.length > 0 && (
              <DividerWrapper>
                <Divider orientation="horizontal" thickness="4px" />
              </DividerWrapper>
            )}
            <Footer spacing="--space-component-lg">
              <PriceAndAction>
                <Stack spacing="--space-component-xs">
                  {footer?.price && (
                    <BoldParagraph size="heading-md">
                      {footer.price}
                    </BoldParagraph>
                  )}
                  {footer?.priceSubheading && (
                    <Audience size="label-sm">
                      {footer?.priceSubheading}
                    </Audience>
                  )}
                </Stack>
                {footer?.CTA?.url && (
                  <Button
                    aria-label={footer?.CTA?.accessibleLabel}
                    href={blockUrlWithAudience(
                      footer?.CTA?.url,
                      footer?.CTA?.audienceRef,
                      pageAudience,
                      footer?.CTA?.noAudienceToggle,
                      isAudienceSwitcherEnabled,
                    )}
                    variant="solid-light"
                    className={
                      footer?.CTA?.trackingTag?.blockName &&
                      `tracking-${footer?.CTA?.trackingTag.blockName}__${footer?.CTA?.trackingTag.blockAction}`
                    }
                    size="lg"
                    iconPosition="right"
                    icon="ArrowRight"
                  >
                    {footer?.CTA?.text}
                  </Button>
                )}
              </PriceAndAction>
              {footer?.description && (
                <Container>
                  <PortableText
                    value={footer.description}
                    audienceConfig={{ pageAudience, isAudienceSwitcherEnabled }}
                  />
                </Container>
              )}
            </Footer>
          </>
        )}
      </SidebarContainer>
    )
  );
};

const ComponentsContainer = styled(Stack)`
  margin-top: var(--space-component-sm);
  margin-bottom: var(--space-component-xl);
`;

const SidebarContainer = styled.div`
  border-radius: var(--radius-component-xl);
  outline: 1px solid rgba(0, 0, 0, 0.12);
  background: var(--background-subtle);
  height: max-content;
  top: 0;
  margin-top: var(--space-block-lg);

  a {
    font-weight: var(--font-weight-medium);
  }

  ${screen.md} {
    position: sticky;
    top: var(--space-block-lg);
    margin-top: var(--space-block-lg);
    margin-left: 40px;
    max-width: 406px;
    box-shadow: 0px 4px 24px 8px rgba(0, 0, 0, 0.04);
  }

  ${screen.lg} {
    max-width: 456px;
    margin-inline: auto;
  }
`;

const Audience = styled(Paragraph)`
  color: var(--text-warm-base);
`;

const PriceAndAction = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const Footer = styled(Stack)`
  padding: var(--space-component-lg) var(--space-page-xs);
  background-color: var(--surface-subtle);
  border-top: 4px solid var(--border-warm-subtle);

  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 1000;

  ${screen.sm} {
    padding: var(--space-component-lg) var(--space-page-sm);
  }

  ${screen.md} {
    position: relative;
    background-color: unset;
    border: none;

    display: flex;
    gap: var(--space-component-lg);
    padding: var(--space-component-xxl) var(--space-component-xxl)
      var(--space-component-xl) var(--space-component-xxl);
  }
`;

const AccordionContainer = styled.div<{ isAccordionPresent: boolean }>`
  padding: ${({ isAccordionPresent }) =>
    isAccordionPresent
      ? css`var(--space-component-lg) var(--space-component-xxl)`
      : css`var(--space-component-xxl) var(--space-component-xxl) var(--space-component-lg)`};

  li {
    ${OuterWrapper} {
      > h1,
      > h2,
      > h3,
      > h4,
      > h5,
      > h6 {
        ${labelLarge};
      }
    }
    button {
      padding: 16px 0;
    }
  }
  > ul > li:not(:last-child) {
    border-bottom: 1px solid var(--border-warm-subtle);
  }

  ${screen.md} {
    overflow-y: scroll;
    max-height: calc(100vh + (calc(var(--navbar-height) + 196px) * -1));
  }
`;

const StyledBulletPoints = styled(BulletPoints)`
  display: flex;
  flex-direction: column;
  gap: var(--space-component-xl);
  p {
    ${bodySmall}
    color: var(--text-warm-subtle);
  }
  li {
    span {
      ${bodyMedium};
    }
    color: var(--text-warm-base);
  }
`;
const Container = styled.div`
  display: none;

  ${screen.md} {
    display: block;

    p {
      ${bodySmall}
      color: var(--text-warm-subtle);
    }
  }
`;

const BoldParagraph = styled(Paragraph)`
  font-weight: var(--font-weight-semibold);
`;

const DividerWrapper = styled.div`
  display: none;
  ${screen.md} {
    display: block;
  }
`;
